import React from 'react';
import type { Leg } from '../types/api';
import { formatDuration, formatDistance, modeIcon, modeColor, formatDelay, delayColor, formatTime, getRouteLabel, getRouteBadgeColor } from '../utils/format';

interface LegDetailProps {
  leg: Leg;
  expanded: boolean;
}

export default function LegDetail({ leg, expanded }: LegDetailProps) {
  const delay = leg.departureDelay ?? leg.start?.estimated?.delay ?? 0;
  const scheduledDepart = leg.start?.scheduledTime;
  const scheduledArrive = leg.end?.scheduledTime;
  const estimatedDepart = leg.start?.estimated?.time;
  const estimatedArrive = leg.end?.estimated?.time;
  const departDelay = leg.start?.estimated?.delay;
  const arriveDelay = leg.end?.estimated?.delay;

  return (
    <div className="leg-detail" style={{ borderLeft: `3px solid ${modeColor(leg.mode)}` }}>
      <div className="leg-summary">
        <span className="leg-icon">{modeIcon(leg.mode)}</span>
        {leg.transitLeg && (
          <span className="leg-route-name" style={{
            background: getRouteBadgeColor(leg),
            color: 'white', padding: '1px 6px', borderRadius: 3, fontSize: 12, fontWeight: 600,
          }}>
            {getRouteLabel(leg)}
          </span>
        )}
        {!leg.transitLeg && <span className="leg-mode">{leg.mode}</span>}
        <span className="leg-from">{leg.from.name}</span>
        <span className="leg-arrow">→</span>
        <span className="leg-to">{leg.to.name}</span>
        <span className="leg-dist">{formatDistance(leg.distance)}</span>
        <span className="leg-dur">{formatDuration(leg.duration)}</span>
      </div>

      {leg.transitLeg && scheduledDepart && (
        <div className="transit-times" style={{ fontSize: 12, marginTop: 2, paddingLeft: 24 }}>
          <span>Dep: {formatTime(scheduledDepart)}</span>
          {departDelay != null && departDelay !== 0 && (
            <span style={{ color: delayColor(departDelay), marginLeft: 6, fontWeight: 600 }}>
              {formatDelay(departDelay)}
            </span>
          )}
          {departDelay != null && departDelay === 0 && estimatedDepart && (
            <span style={{ color: '#22c55e', marginLeft: 4, fontSize: 11 }}>On time</span>
          )}
          {scheduledArrive && (
            <>
              <span style={{ marginLeft: 12 }}>Arr: {formatTime(scheduledArrive)}</span>
              {arriveDelay != null && arriveDelay !== 0 && (
                <span style={{ color: delayColor(arriveDelay), marginLeft: 6, fontWeight: 600 }}>
                  {formatDelay(arriveDelay)}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {expanded && (
        <div className="leg-expanded">
          {leg.mode === 'BICYCLE' && leg.bikeEnrichment && (
            <div className="bike-stats">
              <div>Elevation: +{leg.bikeEnrichment.elevation_gain_m.toFixed(1)}m / -{leg.bikeEnrichment.elevation_loss_m.toFixed(1)}m</div>
              <div>Max grade: {leg.bikeEnrichment.max_grade_pct}%</div>
              <div>Safety score: {leg.bikeEnrichment.safety_score}/100</div>
              <div>Bike infrastructure: {leg.bikeEnrichment.bike_infra_pct}% of route</div>
            </div>
          )}
          {leg.transitLeg && (
            <div className="transit-info">
              {leg.headsign && <div>Headsign: {leg.headsign}</div>}
              {leg.agency && <div>Agency: {leg.agency.name}</div>}
              {leg.alerts && leg.alerts.length > 0 && (
                <div className="alerts">
                  {leg.alerts.map((a, i) => (
                    <div key={i} className="alert-item">{a.alertHeaderText || a.alertDescriptionText}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {leg.mode === 'WALK' && leg.steps && leg.steps.length > 0 && (
            <div className="walk-steps">
              {leg.steps.map((step, i) => (
                <div key={i} className="step">
                  {step.relativeDirection} on {step.streetName} ({formatDistance(step.distance)})
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
